/**
 * Six-line tablature for one voicing, rendered with VexFlow's TabStave.
 *
 * Note the string numbering flip: our voicings are stored low string first
 * (index 0 = low E), while VexFlow numbers strings 1 = high e down to 6 = low E.
 */

import { useEffect, useRef } from 'react';
import { Formatter, Renderer, TabNote, TabStave, Voice } from 'vexflow';
import type { GuitarVoicing } from '../theory/guitar';

export interface GuitarTabProps {
  voicing: GuitarVoicing;
  height?: number;
}

/**
 * Our frets, as VexFlow's tab positions.
 *
 * Pulled out of the render so the numbering flip is testable on its own. It is
 * the one place tablature can be silently wrong: getting it backwards produces a
 * diagram that looks like perfectly ordinary tab and describes a chord upside
 * down. `STANDARD_TUNING[0]` is the low E and VexFlow's string 6 is the bottom
 * line, which is where the low E belongs.
 */
export function tabPositions(voicing: GuitarVoicing): { str: number; fret: string }[] {
  return voicing.frets
    .map((fret, index) => ({ fret, str: 6 - index }))
    .filter((p): p is { fret: number; str: number } => p.fret !== null)
    .map((p) => ({ str: p.str, fret: String(p.fret) }));
}

export function GuitarTab({ voicing, height = 132 }: GuitarTabProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.innerHTML = '';

    const width = Math.max(240, host.clientWidth || 320);
    const renderer = new Renderer(host, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const ctx = renderer.getContext();

    // Scale the context rather than the line spacing — same reason as StaffPaper.
    const scale = 1.5;
    ctx.scale(scale, scale);
    const vw = width / scale;
    const vh = height / scale;

    const stave = new TabStave(4, vh / 2 - 30, vw - 10, { num_lines: 6 });
    stave.addClef('tab');
    stave.setContext(ctx).draw();

    const positions = tabPositions(voicing);

    if (positions.length) {
      const note = new TabNote({ positions, duration: 'w' });
      const voice = new Voice({ num_beats: 4, beat_value: 4 });
      voice.setStrict(false);
      voice.addTickables([note]);
      new Formatter()
        .joinVoices([voice])
        .format([voice], Math.max(60, vw - stave.getNoteStartX() - 20));
      voice.draw(ctx, stave);
    }

    const svg = host.querySelector('svg');
    if (svg) {
      svg.setAttribute('class', 'score-svg');
      svg.style.display = 'block';
    }
  }, [voicing, height]);

  return <div ref={hostRef} className="score" role="img" aria-label="Guitar tablature" />;
}
